
import { useState, useEffect } from 'react';
import { db } from '../db';
import { useLiveQuery } from 'dexie-react-hooks';

/**
 * Hook to check Quota limits.
 * @param {string} resource - 'appointments' | 'patients' | 'clinics' | 'members'
 * @param {string} contextClinicId - Optional. If provided, checks against the Clinic Owner's quota.
 */
export function useQuota(resource, contextClinicId = null) {
    const currentUserId = localStorage.getItem('user_id');

    // 1. Resolve Billable User ID
    const billableUserQuery = useLiveQuery(async () => {
        if (!currentUserId) return null;
        if (contextClinicId) {
            const clinic = await db.clinics.get(contextClinicId);
            return clinic ? clinic.owner_doctor_id : currentUserId;
        }
        return currentUserId;
    }, [currentUserId, contextClinicId]);

    // 2. Fetch Subscription & Plan
    const subscriptionData = useLiveQuery(async () => {
        if (!billableUserQuery) return null;

        // Get Active Subscription
        const sub = await db.user_subscriptions
            .where('user_id').equals(billableUserQuery)
            .filter(s => s.is_active)
            .first();

        if (!sub) return null;

        const plan = await db.plans.get(sub.plan_id);

        // Get Active Addons
        const now = new Date().toISOString();
        const userAddons = await db.user_addons
            .where('user_id').equals(billableUserQuery)
            .filter(ua => !ua.expiry_date || ua.expiry_date > now)
            .toArray();

        const addonDetails = await Promise.all(
            userAddons.map(ua => db.addons.get(ua.addon_id))
        );

        return { plan, addons: addonDetails.filter(Boolean) };
    }, [billableUserQuery]);

    // 3. Calculate Limits & Usage
    const [quotaStatus, setQuotaStatus] = useState({
        loading: true,
        allowed: true,
        limit: 0,
        usage: 0,
        isUnlimited: false
    });

    useEffect(() => {
        const checkUsage = async () => {
            if (!billableUserQuery || !subscriptionData) {
                setQuotaStatus(prev => ({ ...prev, loading: false }));
                return;
            }

            const { plan, addons } = subscriptionData;

            // Calculate Limit
            let limit = 0;
            let period = 'global';
            let isUnlimited = true; // Default to unlimited if not in plan? Or blocked?
            // Based on backend: if not in plan, return True (Unlimited)

            if (plan && plan.quotas && plan.quotas[resource]) {
                isUnlimited = false;
                limit = plan.quotas[resource].limit;
                period = plan.quotas[resource].period;
            }

            // Add-ons
            if (!isUnlimited) {
                addons.forEach(addon => {
                    if (addon.resource === resource) {
                        limit += addon.quantity;
                    }
                });
            }

            // Calculate Usage
            let usage = 0;
            if (!isUnlimited) {
                if (resource === 'appointments') {
                    // Logic must match backend 'get_current_usage'
                    // For appointments, we need to count:
                    // 1. Clinic Appointments (where clinic.owner = billableUser)
                    // 2. Personal Appointments (where doctor_id = billableUser AND clinic_id is null)

                    // Since Dexie doesn't verify ownership easily in one query, we iterate or use multi-query.
                    // Simplified for Client:
                    // If contextClinicId is set, we count appointments in THIS clinic?
                    // NO. Quota is Global or Monthly Limit.
                    // The usage is TOTAL usage of that resource type by the billable user.

                    // Get all clinics owned by billableUser
                    const ownedClinics = await db.clinics.where('owner_doctor_id').equals(billableUserQuery).toArray();
                    const ownedClinicIds = ownedClinics.map(c => c.id);


                    // Dexie filtering
                    const allApts = await db.appointments.toArray(); // optimize?

                    // Filtering in memory for complex logic (indexedDB limits)
                    // Filter: (clinic_id IN ownedClinicIds) OR (doctor_id == billableUser AND clinic_id == null)
                    const ownedApts = allApts.filter(a => {
                        const isClinicApt = a.clinic_id && ownedClinicIds.includes(a.clinic_id);
                        const isPersonalApt = a.doctor_id === billableUserQuery && !a.clinic_id;
                        return isClinicApt || isPersonalApt;
                    });

                    // Period Filter
                    if (period === 'monthly') {
                        const now = new Date();
                        const startOfMonth = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1)).toISOString();
                        usage = ownedApts.filter(a => a.date_time >= startOfMonth).length;
                    } else {
                        usage = ownedApts.length;
                    }

                }
                else if (resource === 'patients') {
                    // Count links where user_id = billableUserQuery
                    let coll = db.patient_doctor_links.where('user_id').equals(billableUserQuery);

                    if (period === 'monthly') {
                        const now = new Date();
                        const startOfMonth = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1)).toISOString();
                        const allLinks = await coll.toArray();
                        // Filter in memory based on created_at
                        usage = allLinks.filter(l => l.created_at >= startOfMonth).length;
                    } else {
                        usage = await coll.count();
                    }
                }
                else if (resource === 'clinics') {
                    let coll = db.clinics.where('owner_doctor_id').equals(billableUserQuery);
                    usage = await coll.count();
                }
                else if (resource === 'members') {
                    // Count users in owned clinics
                    const ownedClinics = await db.clinics.where('owner_doctor_id').equals(billableUserQuery).toArray();
                    const ownedClinicIds = ownedClinics.map(c => c.id);
                    usage = await db.clinic_members.where('clinic_id').anyOf(ownedClinicIds).count();
                    // Note: This counts total memberships, not unique users. Simple enough for now.
                }
            }

            setQuotaStatus({
                loading: false,
                allowed: isUnlimited || usage < limit,
                limit,
                usage,
                isUnlimited
            });
        };

        checkUsage();
    }, [billableUserQuery, subscriptionData, resource]);

    return quotaStatus;
}
